import { memo, useCallback, useEffect, useState } from "react";
import { CreditCard, Gift } from "lucide-react";
import type { GoldenSerenityComponentProps } from "./types";
import { GS, shouldShowGiftRegistry } from "./utils";
import { AmbientGlow, OrganicDivider } from "./decorations";
import { getEventGifts, type GiftCategory } from "@/services/api/gifts";
import { getPublicEventGifts, type PublicGiftItem } from "@/services/api/publicEvents";
import { PublicGiftContributionForm } from "@/components/public/PublicGiftContributionForm";
import { ContributionStatusCheck } from "@/components/public/ContributionStatusCheck";

type ParsedGiftsResponse = {
  gifts: PublicGiftItem[];
  categories: GiftCategory[];
  cardPaymentsEnabled?: boolean;
  stripeAccountId: string | null;
};

const parseGiftsResponse = (response: unknown): ParsedGiftsResponse => {
  const resp = (response ?? {}) as Record<string, unknown>;
  const respData = (resp.data ?? {}) as Record<string, unknown>;
  const giftsList = Array.isArray(resp.gifts)
    ? (resp.gifts as PublicGiftItem[])
    : Array.isArray(respData.gifts)
      ? (respData.gifts as PublicGiftItem[])
      : [];
  const categoriesList = Array.isArray(resp.categories)
    ? (resp.categories as GiftCategory[])
    : Array.isArray(respData.categories)
      ? (respData.categories as GiftCategory[])
      : [];
  return {
    gifts: giftsList.filter((g) => g && (g.enabled === true || g.enabled === undefined)),
    categories: categoriesList,
    // JUB-53 flags (public endpoint only; undefined on the authenticated fallback)
    cardPaymentsEnabled: (resp as { cardPaymentsEnabled?: boolean }).cardPaymentsEnabled,
    stripeAccountId: (resp as { stripeAccountId?: string | null }).stripeAccountId ?? null,
  };
};

export const GoldenSerenityRegistry = memo(({ event, config, locale, t }: GoldenSerenityComponentProps) => {
  const [gifts, setGifts] = useState<PublicGiftItem[]>([]);
  const [categories, setCategories] = useState<GiftCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>("all");
  const [loading, setLoading] = useState(true);
  const [selectedGift, setSelectedGift] = useState<PublicGiftItem | null>(null);
  const [showContributionForm, setShowContributionForm] = useState(false);
  // JUB-53: card payments gate + connected account from the public payload.
  const [cardPaymentsEnabled, setCardPaymentsEnabled] = useState<boolean | undefined>(undefined);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);

  const hasGiftRegistry = shouldShowGiftRegistry(event);

  const fetchGifts = useCallback(() => {
    // Use the public endpoint if we have a URL slug (for public pages);
    // otherwise fall back to the authenticated endpoint (for draft previews).
    return event.url
      ? getPublicEventGifts(event.url)
      : getEventGifts(event.uuid).catch(() => {
          if (event.uuid) {
            return getPublicEventGifts(event.uuid);
          }
          throw new Error("No valid event identifier");
        });
  }, [event.url, event.uuid]);

  useEffect(() => {
    if (!event.registryPublic || !event.giftsActive) {
      setGifts([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchGifts()
      .then((response) => {
        if (cancelled) return;
        const parsed = parseGiftsResponse(response);
        setGifts(parsed.gifts);
        setCategories(parsed.categories);
        setCardPaymentsEnabled(parsed.cardPaymentsEnabled);
        setStripeAccountId(parsed.stripeAccountId);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching gifts:", error);
        setGifts([]);
        setCategories([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event.giftsActive, event.registryPublic, fetchGifts]);

  const handleContributionSuccess = () => {
    // Reload gifts to show updated contribution amounts.
    if (!event.registryPublic || !event.giftsActive) return;
    fetchGifts()
      .then((response) => {
        const parsed = parseGiftsResponse(response);
        setGifts(parsed.gifts);
        setCategories(parsed.categories);
      })
      .catch((error) => {
        console.error("Error refreshing gifts:", error);
      });
  };

  const filteredGifts =
    activeCategory === "all"
      ? gifts
      : gifts.filter((gift) => gift.category?.id === activeCategory || gift.category?.name === activeCategory);

  const allCategories = [{ id: "all", name: t("events.form.template.allItems") }, ...categories];

  // Hide the section entirely if the registry shouldn't be shown.
  if (!hasGiftRegistry) return null;

  return (
    <section
      id="gifts"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.creamLight}, ${GS.creamDeep})` }}
    >
      <AmbientGlow />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10 flex flex-col items-center gap-10 sm:gap-14">
        {/* Section header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Gift className="w-5 h-5" style={{ color: GS.gold }} aria-hidden="true" />
          </div>
          <h2
            className="text-4xl sm:text-6xl font-medium mb-6"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {t("events.form.template.giftRegistry")}
          </h2>
          <p
            className="text-base max-w-2xl mx-auto leading-relaxed"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
          >
            {t("events.form.template.giftRegistryDescription")}
          </p>
        </div>

        {/* Category tabs */}
        {allCategories.length > 1 && (
          <div
            className="flex flex-wrap justify-center gap-x-8 gap-y-3 border-b w-full max-w-2xl pb-4"
            style={{ borderColor: "rgba(232,220,200,0.5)" }}
          >
            {allCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`relative pb-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all ${
                  activeCategory === cat.id ? "" : "opacity-40 hover:opacity-100"
                }`}
                style={{ color: config.palette.text, fontFamily: config.typography.body }}
              >
                {cat.name}
                {activeCategory === cat.id && (
                  <div
                    aria-hidden="true"
                    className="absolute bottom-0 left-0 w-full h-[2px]"
                    style={{ backgroundColor: GS.gold, opacity: 0.6 }}
                  />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Gifts grid */}
        {loading ? (
          <p className="text-sm" style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}>
            {t("common.loading")}
          </p>
        ) : filteredGifts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            {filteredGifts.map((gift) => (
              <div
                key={gift.id}
                className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-500 hover:shadow-[0_10px_36px_rgba(139,115,85,0.16)]"
                style={{
                  background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.creamDeep})`,
                  borderColor: "rgba(232,220,200,0.4)",
                  boxShadow: "0 6px 28px rgba(139,115,85,0.12)",
                }}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  {gift.cover ? (
                    <img
                      src={gift.cover}
                      alt={gift.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: "rgba(212,196,171,0.25)" }}
                    >
                      <Gift className="h-10 w-10 opacity-30" style={{ color: config.palette.text }} aria-hidden="true" />
                    </div>
                  )}
                  <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(to top, rgba(201,168,106,0.10), transparent)" }}
                  />
                </div>

                <div className="p-6 flex flex-col flex-1 gap-4">
                  <div className="flex flex-col gap-1">
                    {gift.category && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-widest"
                        style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                      >
                        {gift.category.name}
                      </span>
                    )}
                    <h3
                      className="text-xl sm:text-2xl font-medium"
                      style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                    >
                      {gift.name}
                    </h3>
                    {gift.description && (
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                      >
                        {gift.description}
                      </p>
                    )}
                  </div>

                  <div className="mt-auto pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedGift(gift);
                        setShowContributionForm(true);
                      }}
                      className="w-full py-3 rounded-full text-sm font-semibold tracking-wide transition-all duration-500 hover:shadow-[0_4px_16px_rgba(201,168,106,0.25)]"
                      style={{
                        background: GS.gold,
                        // JUB-90: creamLight-on-gold was 2.1:1 (fails WCAG AA); GS.ink gives ~5.7:1
                        color: GS.ink,
                        fontFamily: config.typography.body,
                      }}
                    >
                      {t("events.form.template.contributeGift")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Global payment note */}
        <div
          className="w-full max-w-xl text-center p-6 sm:p-8 rounded-2xl border"
          style={{
            background: "rgba(253,248,243,0.6)",
            borderColor: "rgba(232,220,200,0.5)",
          }}
        >
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em] mb-4"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
          >
            {t("events.form.template.universalGiftNote")}
          </p>
          <p
            className="text-sm italic mb-6"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
          >
            {t("events.form.template.universalGiftNoteDescription")}
          </p>
          <div className="flex justify-center opacity-40">
            <CreditCard className="h-8 w-8" style={{ color: config.palette.text }} aria-hidden="true" />
          </div>
        </div>

        {/* JUB-98: guest-visible entry point for the JUB-79 contribution status check */}
        <ContributionStatusCheck
          event={event}
          triggerClassName="text-sm font-medium underline underline-offset-4 opacity-70 hover:opacity-100 transition-opacity"
          triggerStyle={{ color: config.palette.text, fontFamily: config.typography.body }}
        />

        <OrganicDivider />
      </div>

      {/* Gift contribution form */}
      {selectedGift && (
        <PublicGiftContributionForm
          event={event}
          gift={selectedGift}
          config={config}
          locale={locale}
          cardPaymentsEnabled={cardPaymentsEnabled}
          stripeAccountId={stripeAccountId}
          open={showContributionForm}
          onOpenChange={(open) => {
            setShowContributionForm(open);
            if (!open) {
              setSelectedGift(null);
            }
          }}
          onSuccess={handleContributionSuccess}
        />
      )}
    </section>
  );
});

GoldenSerenityRegistry.displayName = "GoldenSerenityRegistry";
